// ---------------------------------------------------------------------------
// FieldEditor — recursive field editor for block config panels.
//
// Supports: string, number, select, boolean, color, object (nested), array.
//
// Hierarchy model — ONE structural language, no competing containers:
//   • A scalar field is a row: label + control (always visible).
//   • A group (object/array) is a collapsible SECTION: a clickable header
//     (rotating chevron + uppercase label), a full-width divider, then its
//     fields indented beneath. Sections START COLLAPSED.
//   • Array items are individually collapsible too (start collapsed), with a
//     summary drawn from the item's label/key so each row is identifiable.
//   • Dividers are the unifying structural element everywhere (title bar,
//     section headers, array items), so the whole panel reads as a single
//     consistent column instead of stacked boxes of differing weight.
// Spacing is owned by the parent list (`space-y-*`); fields carry no margin.
//
// CollapsibleSection / ArrayItem live at module scope (NOT inside FieldEditor)
// so their useState persists across the parent's re-renders — defining them
// inline would mint a new component type on every keystroke and reset state.
// ---------------------------------------------------------------------------

import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { cn } from "@heroui/react";

// First non-empty value among these fields becomes an array item's summary.
// Priority order (label first) so a tile reads "Market cap", not its extract key.
// `value` is last so it only shows when nothing more human-readable is present
// (e.g. a tile with no label still reads its extract key from value.extract).
const SUMMARY_FIELDS = ["label", "title", "name", "key", "text", "heading", "id", "extract", "value"];
// When a candidate field is itself an object (e.g. a tile's label is
// { text: "Market cap" }), descend one level looking for any of these keys.
const NESTED_STRING_KEYS = ["text", "value", "label", "title", "name", "key", "extract", "heading", "id"];

function stringFromValue(v) {
  if (v == null) return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (typeof v === "object") {
    for (const k of NESTED_STRING_KEYS) {
      const sv = v[k];
      if (typeof sv === "string" && sv.trim() !== "") return sv.trim();
    }
  }
  return "";
}

function itemSummary(item) {
  if (!item || typeof item !== "object") return "";
  for (const k of SUMMARY_FIELDS) {
    const s = stringFromValue(item[k]);
    if (s !== "") return s;
  }
  return "";
}

// ── collapsible section (object + array groups) ─────────────────────────
// `action` (e.g. the array "Add" button) is rendered at the right and stops
// propagation so it doesn't toggle the section. The divider always renders so
// collapsed sections still draw a clean boundary.
function CollapsibleSection({ icon, label, count, action, defaultCollapsed = true, children }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <div>
      <div
        className="-mx-1 flex cursor-pointer items-center gap-1.5 rounded-md px-1 py-0.5 transition-colors hover:bg-slate-100"
        onClick={() => setCollapsed((c) => !c)}
      >
        <Icon
          icon={collapsed ? "lucide:chevron-right" : "lucide:chevron-down"}
          className="h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform"
        />
        {icon && <Icon icon={icon} className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        {count != null && (
          <span className="rounded-full bg-slate-100 px-1.5 text-[10px] font-semibold text-slate-500">{count}</span>
        )}
        {action && (
          <div className="ml-auto" onClick={(e) => e.stopPropagation()}>
            {action}
          </div>
        )}
      </div>
      <div className="mb-2 border-t border-slate-200" />
      {!collapsed && children}
    </div>
  );
}

// ── array item (individually collapsible) ───────────────────────────────
function ArrayItem({ idx, item, itemFields, onRemove, onFieldChange }) {
  const [collapsed, setCollapsed] = useState(true);
  const summary = itemSummary(item);
  return (
    <div className={cn("pl-2", idx > 0 && "mt-2 border-t border-slate-200 pt-2")}>
      <div
        className="-mx-1 flex cursor-pointer items-center gap-1.5 rounded-md px-1 py-0.5 transition-colors hover:bg-slate-100"
        onClick={() => setCollapsed((c) => !c)}
      >
        <Icon
          icon={collapsed ? "lucide:chevron-right" : "lucide:chevron-down"}
          className="h-3 w-3 shrink-0 text-slate-400 transition-transform"
        />
        <span className="flex h-4 w-4 items-center justify-center rounded bg-slate-100 text-[9px] text-slate-500">{idx + 1}</span>
        {summary && (
          <span className="truncate text-[12px] font-medium text-slate-600">{summary}</span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="ml-auto flex h-5 w-5 items-center justify-center rounded text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-500"
          title="Remove item"
        >
          <Icon icon="lucide:x" className="h-3.5 w-3.5" />
        </button>
      </div>
      {!collapsed && (
        <div className="mt-2 space-y-2">
          {itemFields.map((f) => (
            <FieldEditor
              key={f.name}
              field={f}
              value={item[f.name]}
              onChange={(subPath, val) => onFieldChange(subPath[subPath.length - 1], val)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── main field renderer ─────────────────────────────────────────────────

function FieldEditor({ field, value, onChange, path = [] }) {
  const currentPath = [...path, field.name];
  const currentValue = value !== undefined && value !== null ? value : (field.defaultValue ?? "");

  const handleChange = (newVal) => {
    onChange(currentPath, newVal);
  };

  const labelEl = (
    <label className="mb-1 block text-[11px] font-medium text-slate-500">
      {field.label || field.name}
    </label>
  );

  const controlCls =
    "w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[13px] text-slate-700 " +
    "outline-none transition-colors placeholder:text-slate-300 " +
    "focus:border-ccgreen-400 focus:ring-2 focus:ring-ccgreen-100";

  // ── simple types ──────────────────────────────────────────────────────

  if (field.type === "string" || field.type === "color") {
    return (
      <div>
        {labelEl}
        <div className="flex items-stretch gap-1.5">
          <input
            type="text"
            value={currentValue === null || currentValue === undefined ? "" : String(currentValue)}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.type === "color" ? "#1F3A2E" : "—"}
            className={cn(controlCls, "flex-1 font-mono text-xs")}
          />
          {field.type === "color" && (
            <div className="relative w-9 shrink-0">
              <input
                type="color"
                value={/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(currentValue || "")) ? currentValue : "#ffffff"}
                onChange={(e) => handleChange(e.target.value)}
                className="absolute inset-0 h-full w-full cursor-pointer rounded-md border border-slate-200 opacity-0"
                title="Pick color"
              />
              <div
                className="pointer-events-none flex h-full w-full items-center justify-center rounded-md border border-slate-200"
                style={{ background: String(currentValue || "transparent") }}
              >
                {!currentValue && <Icon icon="lucide:palette" className="h-3.5 w-3.5 text-slate-400" />}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (field.type === "number") {
    return (
      <div>
        {labelEl}
        <input
          type="number"
          value={currentValue === null || currentValue === undefined ? "" : currentValue}
          onChange={(e) => handleChange(e.target.value === "" ? null : Number(e.target.value))}
          min={field.min} max={field.max} step={field.step}
          className={controlCls}
        />
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div>
        {labelEl}
        <div className="relative">
          <select
            value={String(currentValue ?? "")}
            onChange={(e) => {
              const v = e.target.value;
              if (field.options && field.options.every(o => o === "true" || o === "false")) handleChange(v === "true");
              else handleChange(v);
            }}
            className={cn(controlCls, "appearance-none pr-7")}
          >
            {(field.options || []).map(o => <option key={String(o)} value={String(o)}>{String(o)}</option>)}
          </select>
          <Icon icon="lucide:chevron-down" className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        </div>
      </div>
    );
  }

  if (field.type === "boolean") {
    return (
      <div>
        <label className="flex cursor-pointer items-center gap-2 text-[13px] text-slate-600">
          <span
            onClick={() => handleChange(!currentValue)}
            className={cn(
              "relative h-5 w-9 shrink-0 rounded-full transition-colors",
              currentValue ? "bg-ccgreen-500" : "bg-slate-300"
            )}
          >
            <span className={cn(
              "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform",
              currentValue ? "translate-x-4" : "translate-x-0.5"
            )} />
          </span>
          <span className="text-[13px] text-slate-600">{field.label || field.name}</span>
        </label>
      </div>
    );
  }

  // ── tags (array of strings) ───────────────────────────────────────────
  // For fields like `pins` that are a flat list of string values. Edited as a
  // comma-separated input and persisted as a string[]; the chips below show the
  // resolved values and can be removed individually.
  if (field.type === "tags") {
    const arr = Array.isArray(currentValue) ? currentValue.filter(v => v != null && v !== "") : [];
    return (
      <div>
        {labelEl}
        <input
          type="text"
          value={arr.join(", ")}
          onChange={(e) => {
            const next = e.target.value
              .split(",")
              .map(s => s.trim())
              .filter(s => s !== "");
            handleChange(next);
          }}
          placeholder={field.placeholder || "comma-separated"}
          className={cn(controlCls, "font-mono text-xs")}
        />
        {field.help && (
          <p className="mt-1 text-[10px] leading-snug text-slate-400">{field.help}</p>
        )}
        {arr.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {arr.map((v, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                {v}
                <button
                  onClick={() => handleChange(arr.filter((_, j) => j !== i))}
                  className="text-slate-400 transition-colors hover:text-rose-500"
                  title="Remove"
                >
                  <Icon icon="lucide:x" className="h-2.5 w-2.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── object (nested fields) — collapsible, starts collapsed ───────────

  if (field.type === "object") {
    const subFields = field.fields || [];
    return (
      <CollapsibleSection icon="lucide:braces" label={field.label || field.name}>
        <div className="space-y-2 pl-2">
          {subFields.map(sub => (
            <FieldEditor
              key={sub.name}
              field={sub}
              value={(currentValue && typeof currentValue === "object") ? currentValue[sub.name] : undefined}
              onChange={(subPath, val) => {
                const obj = currentValue && typeof currentValue === "object" ? { ...currentValue } : {};
                obj[subPath[currentPath.length]] = val;
                handleChange(obj);
              }}
              path={currentPath}
            />
          ))}
        </div>
      </CollapsibleSection>
    );
  }

  // ── array of objects — section + individually-collapsible items ──────

  if (field.type === "array") {
    const items = Array.isArray(currentValue) ? currentValue : [];
    const itemSchema = field.itemSchema || {};
    const itemFields = itemSchema.fields || [];

    const addItem = () => {
      const template = {};
      for (const f of itemFields) {
        template[f.name] = f.defaultValue !== undefined ? f.defaultValue : "";
      }
      handleChange([...items, template]);
    };

    const removeItem = (idx) => {
      handleChange(items.filter((_, i) => i !== idx));
    };

    const updateItem = (idx, itemField, val) => {
      const copy = [...items];
      copy[idx] = { ...copy[idx], [itemField]: val };
      handleChange(copy);
    };

    const addAction = (
      <button
        onClick={addItem}
        className="flex items-center gap-1 rounded-md bg-ccgreen-600 px-2 py-0.5 text-[11px] font-medium text-white transition-colors hover:bg-ccgreen-700"
      >
        <Icon icon="lucide:plus" className="h-3 w-3" />
        Add
      </button>
    );

    return (
      <CollapsibleSection icon="lucide:list" label={field.label || field.name} count={items.length} action={addAction}>
        {items.length === 0 && (
          <p className="pl-2 py-1 text-[11px] text-slate-400">No items yet.</p>
        )}
        {items.map((item, idx) => (
          <ArrayItem
            key={idx}
            idx={idx}
            item={item}
            itemFields={itemFields}
            onRemove={() => removeItem(idx)}
            onFieldChange={(fName, val) => updateItem(idx, fName, val)}
          />
        ))}
      </CollapsibleSection>
    );
  }

  return null;
}

export default FieldEditor;
